import * as React from 'react';
import { inject, observer } from 'mobx-react';
import OrderFormFields from '../../components/OrderFormFields';
import OrderFormCart from '../../components/OrderFormCart';
import OrderFormCustomer from '../../components/OrderFormCustomer';
import Spinner from '../../components/Spinner';
import OrderErrors from '../../components/OrderErrors';
import {
  CART_STORE,
  CUSTOMER_STORE,
  ORDER_FORM_STORE,
  NOTIFICATION_STORE,
  ORDER_TYPES_STORE,
  AUTH_STORE,
  MODAL_STORE
} from '../../constants/constants';
import { Cart as ICart, Product, Customer, NavOrderResult, Result } from '../../models/interfaces';
import { CartStore } from '../../stores/CartStore';
import { OrderFormStore } from '../../stores/OrderFormStore';
import { CustomerStore } from '../../stores/CustomerStore';
import { NotificationStore } from '../../stores/NotificationStore';
import { OrderTypesStore } from '../../stores/OrderTypesStore';
import ModalStore from '../../stores/ModalStore';

interface OrderFormProps {
  CartStore: CartStore;
  CustomerStore: CustomerStore;
  NotificationStore: NotificationStore;
  OrderFormStore: OrderFormStore;
  OrderTypesStore: OrderTypesStore;
}

interface OrderFormState {
  showModal: boolean;
  isLoading: boolean;
  modalContent: string;
  modalError: boolean;
  modalSize: string;
  modalOrders: Array<NavOrderResult>;
  modalErrorText: string;
}

@inject(
  CART_STORE,
  CUSTOMER_STORE,
  ORDER_FORM_STORE,
  NOTIFICATION_STORE,
  ORDER_TYPES_STORE,
  AUTH_STORE,
  MODAL_STORE)
@observer
class OrderForm extends React.Component<OrderFormProps, OrderFormState> {

  constructor(props: OrderFormProps) {
    super(props);
    this.state = {
      showModal: false,
      isLoading: false,
      modalContent: '',
      modalError: false,
      modalSize: '',
      modalOrders: [],
      modalErrorText: ''
    };
  }

  async componentDidMount() {
    const orderFormStore = this.props.OrderFormStore;
    const customerStore = this.props.CustomerStore;

    // retrieves user data
    const userData = await this.props[AUTH_STORE].getUser()
      .then(response => {
        return response.json();
      })
      .catch(e => {
        return { status: 'error', message: e };
      });

    // sets userdata if we got something
    if (userData.status && userData.status !== 'error') {
      this.props.OrderFormStore.setContactName(userData.name);
      this.props.OrderFormStore.setContactEmail(userData.mail);

      if (userData.user_name) {
        localStorage.setItem('userName', userData.user_name);
      }

      if (userData.nav_id) {
        localStorage.setItem('userNavId', userData.nav_id);
      }
    }

    // if we have customer id, we add the customer if not selected already and
    // is in customer store
    if (orderFormStore.orderData.CustomerId) {
      orderFormStore.orderData.CustomerId.split(',').map(id => {
        if (!orderFormStore.customers.find(c => c.No === id)) {
          let customer = customerStore.customers.find(c => c.No === id);
          if (customer) {
            orderFormStore.addCustomer(customer);
          }
        }
      });
    }
  }

  async createOrder() {
    const modalStore = this.props[MODAL_STORE] as ModalStore;

    modalStore.setTitle(<h2>Tilausta käsitellään</h2>);
    modalStore.setChildren(<Spinner />);
    modalStore.onlyCloseButton = true;
    modalStore.openModal();

    const result = await this.props.OrderFormStore
      .createOrder(this.props.CartStore.cart.products)
      .then(async results => {

        let _result: Result = {
          status: 'ok', message: '', orders: []
        };

        await Promise.all(results.map(async res => {
          const data = await res.fetchResponse.json();
          if (data.error) {
            _result.status = 'error';
            _result.message = data.error;
            _result.orders.push({ navOrder: res.order, status: 'error' });
          } else {
            _result.orders.push({ navOrder: res.order, status: 'success' });
          }
        }));

        return _result;
      })
      .catch(err => {
        return {
          status: 'error',
          message: err.message + ': Tilauksen käsittelyssä havaittiin virhe.',
          orders: []
        } as Result;
      });

    if (result.status !== ('error' || 500)) {

      if (this.props[ORDER_FORM_STORE].orderData.EmailConfirmation) {
        this.props[ORDER_FORM_STORE].sendEmailConfirmation(result.orders);
      }

      this.props.CartStore.clearStore();
      this.props.OrderFormStore.clearStore();

      modalStore.setTitle(<h2>Tilaus tehty!</h2>);
      modalStore.setChildren(undefined);

    } else {
      // loop orders and find orders(customers) that went through and remove them from store
      let customers: Array<Customer> = [];
      let modalErrorText = '';

      result.orders.map(o => {

        if (o.status === ('error' || 500)) {
          customers.push({
            Name: o.navOrder.Name,
            Name2: o.navOrder.Name2,
            No: o.navOrder.CustomerId,
            Address: o.navOrder.ShipAddr1,
            City: o.navOrder.ShipCity,
            Post_Code: o.navOrder.ShipZip,
            Key: '',
            company: ''
          });
          modalErrorText = `
            Punaisilla merkittyjä tilauksia ei käsitelty onnistuneesti.
            Kokeile lähettää tilaukset uudestaan.
            Onnistuneesti lähetyt tilaukset on poistettu asiakaslistauksesta.
          `;
        }
      });

      this.props.OrderFormStore.setCustomers(customers);

      modalStore.setSize('large');
      modalStore.setTitle('Virhe tilauksen käsittelyssä: ' + result.message);
      modalStore.addError(modalErrorText);
      modalStore.setChildren(<OrderErrors orders={result.orders} />);
    }

    this.props[ORDER_FORM_STORE].setOrdering(false);
  }

  validateAndCreateOrder() {
    if (this.isCartEmpty()) {
      this.props[NOTIFICATION_STORE].addNotification({
        type: 'error',
        message: 'Tilauksesi on tyhjä',
        seen: false
      });
    } else {
      this.props[ORDER_FORM_STORE].setProducts(this.props.CartStore.cart.products);
      this.props[ORDER_FORM_STORE].setCustomersData();
      const errors = this.props[ORDER_FORM_STORE].validateOrder();

      if (errors.length === 0 && !this.props[ORDER_FORM_STORE].ordering) {
        this.createOrder();
      } else {
        // Show errors for user.
        errors.map(error => {
          this.props[NOTIFICATION_STORE].addNotification(error);
        });
      }
    }
  }

  isCartEmpty() {
    return this.props.CartStore.cart.products.length === 0 ? true : false;
  }

  render() {
    const cartStore = this.props[CART_STORE];
    const customerStore = this.props[CUSTOMER_STORE];
    const orderFormStore = this.props[ORDER_FORM_STORE];
    const notificationStore = this.props[NOTIFICATION_STORE];

    const cart = this.props[CART_STORE].cart as ICart;
    const customers = this.props[CUSTOMER_STORE].allCustomers as Array<Customer>;
    const orderTypes = this.props[ORDER_TYPES_STORE].orderTypes;
    const selectedCustomers = orderFormStore.customers;

    const selectCustomerAutomatically = () => {
      const userEmail = orderFormStore.orderData.EMail;
      const userName = localStorage.getItem('userName');
      const userNavId = localStorage.getItem('userNavId');

      let customer = customerStore.customers.find(c => {

        if (c.E_Mail === userEmail) {
          return true;
        } else if (userNavId && userNavId === c.No) {
          return true;
        }

        return false;
      });

      if (customer) {
        let { No } = customer;
        if (!orderFormStore.customers.find(c => c.No === No)) {
          orderFormStore.addCustomer(customer);
        }
      }
    };

    return (
      <div className="section section--order-form">
        <div className="order-form condensed">
          <OrderFormCart
            cartProducts={cart.products}
            addProduct={(product: Product, amount: number) => cartStore.addProduct(product, amount)}
            removeProduct={(product: Product) => cartStore.removeProduct(product)}
            subtractProduct={(product: Product, amount: number) => cartStore.subtractProduct(product, amount)}
            changeProductQuantity={(product, quantity) => cartStore.setProductQuantity(product, quantity)}
          />
          <OrderFormFields
            cart={cart}
            orderFormStore={orderFormStore}
            notificationStore={notificationStore}
            orderTypes={orderTypes}
            selectCustomerAutomatic={() => selectCustomerAutomatically()}
          />
          <OrderFormCustomer
            customers={customers}
            cartProducts={cart.products}
            orderFormStore={orderFormStore}
            selectedCustomers={selectedCustomers}
          />
          <input
            className={this.isCartEmpty() ? 'disabled' : ''}
            type="submit"
            value="Lähetä tilaus"
            onClick={() => this.validateAndCreateOrder()}
          />
        </div>
      </div>
    );
  }
}

export default OrderForm;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/order-form/OrderForm.tsx